/**
 * FinderPage — shareable preset URLs that auto-submit the quiz
 * and land the user on a Results page with the chosen refinements.
 *
 * Example URLs:
 *   /finder/stability-wide-runners
 *   /finder/cushioned-marathon
 *   /finder/everyday-casual-black
 *
 * Each preset maps to a quiz payload + optional refinement chips.
 * Marketing can share these links anywhere (TikTok, Reddit, blog posts)
 * and visitors land directly on a curated picks page with brand-correct
 * AI matches in under 1 second.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CircleNotch, Warning, ArrowRight } from '@phosphor-icons/react';
import { trackQuizCompleted } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset library. Adding a new preset = adding one entry here.
const PRESETS = {
  'stability-wide-runners': {
    title: 'Stability Runners for Wide Feet',
    blurb: 'Top stability picks with extra width — built for flat-arched runners.',
    quiz: {
      gender: 'mens', activity: 'running', foot_width: 'E',
      arch_type: 'low', size: '10', budget: '',
      preferred_brands: [], preferred_colors: [],
    },
    refine: [],
  },
  'cushioned-marathon': {
    title: 'Cushioned Marathon Picks',
    blurb: 'Max-cushion daily trainers for long-distance runners with high arches.',
    quiz: {
      gender: 'mens', activity: 'running', foot_width: 'D',
      arch_type: 'high', size: '10', budget: '150-200',
      preferred_brands: [], preferred_colors: [],
    },
    refine: ['cushion'],
  },
  'everyday-casual-black': {
    title: 'Everyday Casual — Black',
    blurb: 'All-black casual sneakers for daily wear.',
    quiz: {
      gender: 'unisex', activity: 'casual', foot_width: 'D',
      arch_type: 'normal', size: '9', budget: '100-150',
      preferred_brands: [], preferred_colors: ['black'],
    },
    refine: [],
  },
  'kids-school-black': {
    title: 'Black School Shoes for Kids',
    blurb: 'Durable black school shoes from trusted brands.',
    quiz: {
      gender: 'unisex', activity: 'school', foot_width: 'D',
      arch_type: 'normal', size: '5', budget: '',
      preferred_brands: [], preferred_colors: ['black'],
    },
    refine: [],
  },
  'womens-running-stability': {
    title: 'Stability Runners for Women',
    blurb: 'Womens stability shoes that support flat or low arches.',
    quiz: {
      gender: 'womens', activity: 'running', foot_width: 'B',
      arch_type: 'low', size: '7', budget: '',
      preferred_brands: [], preferred_colors: [],
    },
    refine: ['stability'],
  },
  'hiking-day-trips': {
    title: 'Day Hiking Shoes',
    blurb: 'Trail-ready picks for weekend hikers and day trippers.',
    quiz: {
      gender: 'unisex', activity: 'hiking', foot_width: 'D',
      arch_type: 'normal', size: '9', budget: '100-150',
      preferred_brands: [], preferred_colors: [],
    },
    refine: [],
  },
  'budget-running-under-100': {
    title: 'Running Shoes Under $100',
    blurb: 'Solid daily-trainer picks that wont break the bank.',
    quiz: {
      gender: 'mens', activity: 'running', foot_width: 'D',
      arch_type: 'normal', size: '10', budget: '50-100',
      preferred_brands: [], preferred_colors: [],
    },
    refine: ['budget-low'],
  },
  'basketball-court-ready': {
    title: 'Court-Ready Basketball',
    blurb: 'Best-rated mens basketball shoes for game day.',
    quiz: {
      gender: 'mens', activity: 'basketball', foot_width: 'D',
      arch_type: 'normal', size: '10', budget: '',
      preferred_brands: [], preferred_colors: [],
    },
    refine: [],
  },
};

export default function FinderPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const preset = PRESETS[slug];

  useEffect(() => {
    let cancelled = false;
    if (!preset) return;

    (async () => {
      try {
        const res = await fetch(`${API}/quiz/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify(preset.quiz),
        });
        if (!res.ok) throw new Error(`Server ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        // GA: track quiz completion via preset-based finder flow
        trackQuizCompleted({
          region: preset.quiz?.region,
          activity: preset.quiz?.activity,
          footWidth: preset.quiz?.foot_width,
          archType: preset.quiz?.arch_type,
          budget: preset.quiz?.budget,
          gender: preset.quiz?.gender,
          recCount: data.recommendations?.length,
        });

        const refineParam = preset.refine.length ? `?refine=${preset.refine.join(',')}` : '';
        navigate(`/results/${data.profile_id}${refineParam}`, {
          replace: true,
          state: { results: data },
        });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load preset');
      }
    })();

    return () => { cancelled = true; };
  }, [slug, preset, navigate]);

  if (!preset) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="finder-notfound">
        <div className="max-w-md text-center">
          <Warning size={40} className="text-[#FF3B30] mx-auto mb-4" />
          <h1 className="font-heading font-black text-2xl uppercase tracking-tighter mb-2">
            Preset not found
          </h1>
          <p className="text-[#52525B] mb-6">
            We dont have a preset for <span className="font-mono text-[#0A0A0A]">{slug}</span>.
            Try the full fit quiz instead.
          </p>
          <Link
            to="/quiz"
            className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#D62828] transition-colors"
            data-testid="finder-fallback-quiz">
            Take the fit quiz <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 gap-6"
      data-testid="finder-loading">
      <CircleNotch size={48} className="animate-spin text-[#FF3B30]" />
      <div className="text-center max-w-md">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">
          Curated picks loading
        </p>
        <h1 className="font-heading font-black text-2xl sm:text-3xl uppercase tracking-tighter mb-2">
          {preset.title}
        </h1>
        <p className="text-[#52525B]">{preset.blurb}</p>
        {error && (
          <p className="mt-4 text-sm text-[#FF3B30]" data-testid="finder-error">{error}</p>
        )}
      </div>
    </div>
  );
}
